import React, { useState, useEffect, useMemo } from 'react'
import CircularProgress from '@/components/CircularProgress'
import { getClientResultsSIG, formatCurrency as realFormatCurrency } from '@/lib/real-data-service'
import { useAuth } from '@/hooks/useAuth'
import { Building2, Users, Globe, Shield, Truck } from 'lucide-react'

export function AcompanhamentoTab() {
  const { user: authUser } = useAuth();
  const [clientData, setClientData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load client results data asynchronously
  useEffect(() => {
    const loadClientData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Check if getClientResultsSIG is async or sync
        const results = await Promise.resolve(getClientResultsSIG());
        setClientData(results);
      } catch (err) {
        console.error('Error loading client results:', err);
        setError('Falha ao carregar resultados dos clientes');
        setClientData([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadClientData();
  }, []);

  // 💼 PERSONALIZED CLIENT RESULTS - Real data from RECEITAS JULHO 2025.xlsx with actual logos
  const clientResults = useMemo(() => {
    return clientData
      .filter(result => {
        // Only admins can see PARCERIAS data
        if (result.id === 'parcerias' && authUser?.role !== 'admin') {
          return false;
        }
        return true;
      })
      .map(result => ({
        ...result,
        // Map fallback icons to components for clients without logos
        fallbackIconComponent: result.fallbackIcon === 'Building2' ? Building2 :
                              result.fallbackIcon === 'Users' ? Users :
                              result.fallbackIcon === 'Globe' ? Globe :
                              result.fallbackIcon === 'Shield' ? Shield :
                              result.fallbackIcon === 'Truck' ? Truck :
                              Building2 // Default fallback
      }));
  }, [clientData, authUser]);

  const formatCurrency = realFormatCurrency

  if (isLoading) {
    return (
      <div className="space-y-8 animate-fade-in-up">
        <div className="card">
          <h3 className="font-display text-2xl font-bold text-white mb-8 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
            Resultado dos Clientes
          </h3>
          <div className="flex justify-center items-center min-h-[300px]">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-brand-gray-light font-body">Carregando acompanhamento dos clientes...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8 animate-fade-in-up">
        <div className="card">
          <h3 className="font-display text-2xl font-bold text-white mb-8 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
            Resultado dos Clientes
          </h3>
          <div className="flex justify-center items-center min-h-[300px]">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-red-500 text-2xl"></span>
              </div>
              <p className="text-red-400 font-body mb-2">Erro ao carregar dados</p>
              <p className="text-brand-gray-light font-body text-sm">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-up">
      {/* Client Results - Real data from RECEITAS JULHO 2025.xlsx with actual client logos
           PARCERIAS data is restricted to admin users only */}
      <div className="card">
        <h3 className="font-display text-2xl font-bold text-white mb-8 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">
          📊 Resultado dos Clientes
        </h3>
        {clientResults.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {clientResults.map((client) => {
            const FallbackIcon = client.fallbackIconComponent
            const isHighPerformance = client.performance >= 80

            return (
              <div key={client.id} className={`card-interactive group p-6 transition-all duration-200 ${isHighPerformance ? 'border-success/30 bg-success/5' : ''}`}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-monumenta bg-brand-primary/10 group-hover:bg-brand-primary/20 transition-colors duration-200">
                      <FallbackIcon className="w-6 h-6 text-brand-primary group-hover:scale-110 transition-transform duration-200" />
                    </div>
                    <h3 className="font-display text-lg font-bold text-white group-hover:text-brand-primary transition-colors duration-200">{client.nome}</h3>
                  </div>
                  <div className="group-hover:scale-105 transition-transform duration-200">
                    <CircularProgress percentage={client.performance} />
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-3 bg-brand-dark/30 rounded-monumenta">
                    <span className="text-sm text-brand-gray-light font-body">Realizado</span>
                    <span className="text-sm text-white font-display font-bold">
                      {formatCurrency(client.valor)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center p-3 bg-brand-dark/30 rounded-monumenta">
                    <span className="text-sm text-brand-gray-light font-body">PROA</span>
                    <span className="text-sm text-brand-gray-light font-display font-semibold">
                      {formatCurrency(client.proa)}
                    </span>
                  </div>
                  
                  {/* Performance indicator */}
                  <div className="pt-2">
                    <div className="w-full bg-brand-dark-lighter rounded-monumenta h-2 shadow-inner">
                      <div 
                        className={`h-2 rounded-monumenta transition-all duration-500 shadow-monumenta ${
                          isHighPerformance ? 'bg-gradient-to-r from-success to-success/80' : 'bg-gradient-to-r from-brand-primary to-brand-accent-600'
                        }`}
                        style={{ width: `${client.performance}%` }}
                      />
                    </div>
                    <p className="text-xs text-brand-gray font-body mt-1 text-center">
                      Performance: {client.performance}%
                    </p>
                  </div>
                </div>
              </div>
            )
          })}
          </div>
        ) : (
          <div className="flex justify-center items-center min-h-[200px]">
            <p className="text-brand-gray-light font-body">Nenhum dado de acompanhamento disponível</p>
          </div>
        )}
      </div>
    </div>
  )
}